// @flow

import React, { useCallback } from 'react';
import type { Node } from 'react';
import styled from 'styled-components';

import { Color, h242428, Width } from 'styles';
import { PlusAccordion, Anchor, Link } from 'components/Gui';
import { RightChevronArrow } from 'components/Svg';
import { isKeyboard, labelToId } from 'utils';
import { BODY_ID } from 'Constants';

const ContentItem = styled.section`
  border-top: 2px solid #e8e8e8;
  padding-top: 40px;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    display: flex;
  }

  h2 {
    ${h242428};
    line-height: 1;
    margin: 0;
    text-align: center;

    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      white-space: unset;
    }
  }

  > div {
    &:nth-of-type(1) {
      flex: 0 0 auto;
      width: 100%;
      display: flex;
      flex-direction: column;

      @media print, screen and (min-width: ${Width.BREAK_MD}px) {
        width: 234px;
        padding-right: 24px;
        box-sizing: border-box;
      }

      @media print, screen and (min-width: ${Width.BREAK_XL}px) {
        width: 45%;
        flex: 0 0 auto;
        padding-right: 176px;
      }

      h2 {
        order: 1;

        @media print, screen and (min-width: ${Width.BREAK_MD}px) {
          order: unset;
        }
      }

      a {
        display: ${({ index }) => (index === 0 ? 'none' : 'inline-flex')};
        align-items: center;
        font-size: 1.5rem;
        line-height: 1;
        margin-bottom: 20px;
        letter-spacing: 0.05rem;
        text-decoration: none;

        @media print, screen and (min-width: ${Width.BREAK_MD}px) {
          margin-top: 38px;
        }

        span {
          margin-left: 16px;
          font-weight: 400;
          color: ${Color.BLACK_SOUL};
        }

        svg {
          width: 7px;
          transform: translateY(-1px) rotate(-90deg);
          transform-origin: 50% 50% 0;
        }
      }
    }
  }
`;

const Question = styled.div`
  padding: 24px 0;
  border-top: 2px solid #e8e8e8;

  &:first-of-type {
    border-top: none;

    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      padding-top: 0;
    }
  }
`;

export const TwoColumnAccordionList = ({
  faqSectionID,
  rootElementId,
  content,
}: {
  rootElementId?: string,
  faqSectionID?: string,
  content: {
    title: string,
    content: {
      q: string | Node,
      a: string | Node,
    }[],
  }[],
}) => {
  const onClick = useCallback(() => {
    if (isKeyboard()) {
      const els = document.getElementById(BODY_ID).querySelectorAll('[href]');
      if (els[0]) els[0].focus();
    }
  }, []);
  return (
    <>
      {content.map(({ title, content: questions }, index) => (
        <ContentItem key={`ContentItem-${index}`} index={index}>
          <Anchor id={labelToId(title)} />
          <div>
            <h2>{title}</h2>
            <Link url={`#${rootElementId}`} onClick={onClick}>
              {RightChevronArrow}
              <span>Back to top</span>
            </Link>
          </div>
          <div id={faqSectionID ? `${faqSectionID}-${index}` : null}>
            {questions.map(({ q, a }, innerIndex) => (
              <Question key={`Questions-${index}-${innerIndex}`}>
                <PlusAccordion a={a} q={q} />
              </Question>
            ))}
          </div>
        </ContentItem>
      ))}
    </>
  );
};

export default TwoColumnAccordionList;
